import { FinanceFactSummariesQuery, InsightTableItemsQuery } from 'generated/resources/graphql'

import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'
import {
  groupChildrenByCategory,
  groupRecordsByCategory,
  mergeComparisonDataByIds,
} from 'modules/finance/components/FinancialWorkspaceDataCollection/utils/transformData'

import { transformItemToRecord } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/transformRecords'

/**
 * Transforms FinanceFactSummaries query data with category grouping
 *
 * Children are always pre-loaded from the backend for Compensation tables.
 *
 * When groupBy is LineItem: Group flat items by categoryLabel (e.g., "Earnings", "Deductions")
 * When groupBy is other (Employee, LegalEntity, etc.): Group children by categoryLabel
 *   - Expanding a parent shows category groups (Earnings, Deductions, Taxes)
 *   - Expanding a category shows individual line items
 *
 * @param comparisonMap - Optional map of comparison records by ID for merging comparison values
 */
export const mapFinanceFactSummariesData = (
  level: number,
  data: FinanceFactSummariesQuery,
  groupBy: InsightTableGroupBy,
  comparisonData?: Record<string | number, FinancialWorkspaceRecord | null>,
): FinancialWorkspaceRecord[] => {
  const edges = data.financialWorkspace.financeFactSummariesConnection.edges

  let records = edges.map((edge) => {
    const record = transformItemToRecord({
      level,
      id: edge.node.id,
      rows: edge.node.rows,
      label: edge.node.label,
      resourceId: edge.node.id, // Set resource ID for children rows to infer
      active: edge.node.active,
      children: edge.node.children,
      category: edge.node.category,
      imageUrl: edge.node.imageUrl,
      hasChildren: edge.node.hasChildren,
      categoryLabel: edge.node.categoryLabel
    })

    return record
  })

  // Merge comparison data if available
  const comparisonRecords = Object.values(comparisonData ?? {}).filter(Boolean)
  if (comparisonRecords.length > 0) {
    records = mergeComparisonDataByIds(records, comparisonRecords)
  }

  // Apply category grouping based on groupBy
  if (groupBy === InsightTableGroupBy.LineItem) {
    // For LineItem view, group the flat list of items by categoryLabel
    return groupRecordsByCategory(records, level)
  } else {
    // For other views (Employee, LegalEntity, etc.), group the children of each record
    // This creates nested structure: Parent → Category → LineItems
    return groupChildrenByCategory(records, level)
  }
}

/**
 * Transforms InsightTableItems query data with category grouping
 *
 * When groupBy is LineItem: Group flat items by categoryLabel
 * When groupBy is other (Employee, LegalEntity, etc.): Group children by categoryLabel
 *
 * @param comparisonData - Optional map of comparison records by ID for merging comparison values
 */
export const mapInsightTableItemsData = (
  level: number,
  data: InsightTableItemsQuery,
  groupBy: InsightTableGroupBy,
  comparisonData?: Record<string | number, FinancialWorkspaceRecord | null>
): FinancialWorkspaceRecord[] => {
  const edges = data.financialWorkspace.insightTableItemsConnection.edges
  let records = edges.map((edge) =>
    transformItemToRecord({
      level,
      id: edge.node.id,
      rows: edge.node.rows,
      label: edge.node.label,
      active: edge.node.active,
      resourceId: edge.node.id, // Set resource ID for children rows to infer
      children: edge.node.children,
      category: edge.node.category,
      imageUrl: edge.node.imageUrl,
      categoryLabel: edge.node.categoryLabel
    })
  )

  // Merge comparison data if available
  const comparisonRecords = Object.values(comparisonData ?? {}).filter(Boolean)
  if (comparisonRecords.length > 0) {
    records = mergeComparisonDataByIds(records, comparisonRecords)
  }

  // Apply category grouping based on groupBy
  if (groupBy === InsightTableGroupBy.LineItem) {
    // For LineItem view, group the flat list of items by categoryLabel
    return groupRecordsByCategory(records, level)
  } else {
    // For other views (Employee, LegalEntity, etc.), group the children of each record
    return groupChildrenByCategory(records, level)
  }
}
